import { Input, Node, Output } from "rete"
import { NodeData, WorkerInputs, WorkerOutputs } from "rete/types/core/data"
import { categoricalDataSocket, tableDataSocket } from "../socket_types"
import { CategoricalTileGrid } from "../tile_grid"
import { TableDataset } from "../table_dataset"
import {
  TableEditorControl,
  TABLE_CELLS_KEY,
  TABLE_COLUMNS_KEY,
  TABLE_OPEN_KEY,
  TABLE_ROWS_KEY,
  TableCell,
} from "../controls/table_editor"
import { ExposeToggleControl } from "../controls/expose_toggle_control"
import { BaseComponent } from "./base_component"
import { getCategoricalLabelsFromInputChain } from "./categorical_label_chain"
import { isCategoricalChainSeedingSuppressed } from "./categorical_dynamic_io_connections"

const REF_INPUT_KEY = "ref"
const TABLE_CONTROL_KEY = "tableEditor"

function readStringArray(value: unknown): string[] {
  return Array.isArray(value) ? value.map(v => String(v)) : []
}

function readCells(value: unknown): TableCell[][] {
  return Array.isArray(value) ? (value as TableCell[][]) : []
}

function arraysEqual(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((v, i) => v === b[i])
}

/**
 * Replace the table's columns with `newColumns`, preserving any existing cell
 * values by matching on the previous column name. Cells under columns that no
 * longer exist are dropped; new columns are filled with `null`. Returns `true`
 * when the columns actually changed.
 */
function setTableColumns(node: Node | NodeData, newColumns: string[]): boolean {
  const oldColumns = readStringArray(node.data[TABLE_COLUMNS_KEY])
  if (arraysEqual(oldColumns, newColumns)) return false

  const rows = readStringArray(node.data[TABLE_ROWS_KEY])
  const oldCells = readCells(node.data[TABLE_CELLS_KEY])

  const oldIndexByName = new Map<string, number>()
  oldColumns.forEach((name, i) => {
    if (!oldIndexByName.has(name)) oldIndexByName.set(name, i)
  })

  node.data[TABLE_COLUMNS_KEY] = newColumns
  node.data[TABLE_CELLS_KEY] = rows.map((_r, r) =>
    newColumns.map(colName => {
      const oldC = oldIndexByName.get(colName)
      if (oldC === undefined) return null
      const v = oldCells[r] ? oldCells[r][oldC] : null
      return typeof v === "number" && Number.isFinite(v) ? v : null
    })
  )
  return true
}

/** Ordered category labels of a categorical grid, excluding the no-data value (255). */
function extractColumnLabels(grid: CategoricalTileGrid): string[] {
  const map = grid.labels ?? new Map<number, string>()
  return Array.from(map.entries())
    .filter(([value]) => value !== 255)
    .sort((a, b) => a[0] - b[0])
    .map(([value, label]) => (typeof label === "string" && label.trim() ? label.trim() : `Category ${value}`))
}

export class CreateEmptyTableComponent extends BaseComponent {
  private static connectionHooksByNodeId = new Set<number>()

  constructor() {
    super("New Table")
    this.category = "Inputs"
    this.description =
      "Creates or loads a Table dataset — tabular data with named rows and columns, not linked to the map. Use it as a lookup table to assign numeric values to categorical classes. Click 'Show table' to edit the grid, or import and export CSV/XLSX from the toolbar. Optionally connect a Categorical dataset to the reference input to set columns from its class labels."
  }

  async builder(node: Node) {
    if (node.data[TABLE_ROWS_KEY] === undefined) node.data[TABLE_ROWS_KEY] = []
    if (node.data[TABLE_COLUMNS_KEY] === undefined) node.data[TABLE_COLUMNS_KEY] = []
    if (node.data[TABLE_CELLS_KEY] === undefined) node.data[TABLE_CELLS_KEY] = []
    if (node.data[TABLE_OPEN_KEY] === undefined) node.data[TABLE_OPEN_KEY] = false

    node.addInput(new Input(REF_INPUT_KEY, "Reference dataset (optional)", categoricalDataSocket))

    if (!("expose" in node.data)) {
      node.data.expose = node.data.showInDetail === true
    }
    if (!node.controls.has("expose")) {
      node.addControl(new ExposeToggleControl(this.editor, "expose"))
    }

    node.addControl(new TableEditorControl(this.editor, TABLE_CONTROL_KEY, REF_INPUT_KEY))
    node.addOutput(new Output("out", "Table", tableDataSocket))

    // Reflect an already-wired reference (e.g. a freshly dropped connection);
    // skipped during fromJSON replay so saved columns are kept.
    this.refreshFromReference(node)

    if (this.editor && !CreateEmptyTableComponent.connectionHooksByNodeId.has(node.id)) {
      CreateEmptyTableComponent.connectionHooksByNodeId.add(node.id)
      const refresh = () => this.refreshFromReference(node)
      this.editor.on("connectioncreated", refresh)
      this.editor.on("connectionremoved", refresh)
      this.editor.on("noderemoved", (removed: any) => {
        if (removed?.id === node.id) {
          CreateEmptyTableComponent.connectionHooksByNodeId.delete(node.id)
        }
      })
    }
  }

  /** Force the table control to re-render (it reads node.data imperatively). */
  private updateControl(node: Node) {
    const control = node.controls.get(TABLE_CONTROL_KEY) as { update?: () => void } | undefined
    if (control && typeof control.update === "function") control.update()
  }

  private refreshFromReference(node: Node) {
    if (!this.editor) return
    if (isCategoricalChainSeedingSuppressed()) return

    const refInput = node.inputs.get(REF_INPUT_KEY)
    const attached = !!refInput && refInput.connections.length > 0

    if (attached) {
      const rows = getCategoricalLabelsFromInputChain(this.editor, node, REF_INPUT_KEY)
      if (rows.length > 0) {
        setTableColumns(node, rows.map(r => r.label))
      }
    }

    // Always re-render so the lock state (and any new columns) is reflected
    // immediately when the reference connection changes.
    this.updateControl(node)
  }

  worker(node: NodeData, inputs: WorkerInputs, outputs: WorkerOutputs) {
    const editorNode = this.editor?.nodes.find(n => n.id === node.id)
    const dataNode: Node | NodeData = editorNode ?? node

    // The connected categorical grid is the authoritative source for columns —
    // set them from its real labels when present (the connection-time chain
    // walk may not have resolved them yet).
    const ref = inputs[REF_INPUT_KEY]?.[0]
    if (ref instanceof CategoricalTileGrid) {
      if (setTableColumns(dataNode, extractColumnLabels(ref)) && editorNode) {
        this.updateControl(editorNode)
      }
    }

    const rows = readStringArray(dataNode.data[TABLE_ROWS_KEY])
    const columns = readStringArray(dataNode.data[TABLE_COLUMNS_KEY])
    const rawCells = readCells(dataNode.data[TABLE_CELLS_KEY])

    const data = rows.map((_r, r) =>
      columns.map((_c, c) => {
        const value = rawCells[r] ? rawCells[r][c] : null
        return typeof value === "number" && Number.isFinite(value) ? value : NaN
      })
    )

    const table = new TableDataset(rows, columns, data)
    table.name = (dataNode.data.name as string) || "Table"

    outputs.out = table
  }
}
